import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, Check, X } from 'lucide-react';
import axios from 'axios';
import { NON_REPAIRABLE_DEVICE_STATUSES, INACTIVE_DEVICE_STATUSES, formatDeviceStatus } from '../../utils/statusUtils';
// Imported straight from the module: the ./ui barrel re-exports SearchFilterBar,
// which imports this component, so going through it would be a circular import.
import StatusBadge from './ui/StatusBadge';

// Module-level storage to persist across component remounts
const lastSubmittedValues = new Map();

const DeviceAutocomplete = React.forwardRef(({ 
  value, 
  onChange, 
  onDeviceSelect, // Kept for backward compatibility, now generic
  placeholder = "Enter device serial number",
  required = false,
  disabled = false,
  searchType = 'devices', // 'devices' | 'products'
  productId = null, // Optional: filter devices by product ID (only for searchType='devices')
  onKeyPress = null, // Optional: handle key press events (e.g., Enter to submit)
  onClear = null, // Optional: callback after clearing the search value
  size = 'md', // 'sm' | 'md'
  disableNonRepairable = false, // Optional: show retired/lost/blocked/trashed devices in red and prevent selecting them
  includeAllStatuses = false // Optional: also return retired/donated/trashed devices, labelled with their status
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [devices, setDevices] = useState([]); // Generic name kept for backward compatibility
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [selectedIndex, setSelectedIndex] = useState(-1); // -1 means no selection
  const inputRef = useRef(null);
  const setInputRef = (el) => {
    inputRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };
  const dropdownRef = useRef(null);
  const itemRefs = useRef([]); // Refs for each dropdown item for scrolling
  // Used to skip one search cycle after a user selects an item from the dropdown
  const suppressNextSearchRef = useRef(false);
  // Create a unique key for this autocomplete instance based on searchType and productId
  const instanceKey = `${searchType}-${productId || 'none'}`;
  
  // Get last submitted value from module-level storage (persists across remounts)
  const getLastSubmittedValue = () => lastSubmittedValues.get(instanceKey) || null;
  const setLastSubmittedValue = (val) => {
    if (val === null) {
      lastSubmittedValues.delete(instanceKey);
    } else {
      lastSubmittedValues.set(instanceKey, val);
    }
  };
  

  // Update dropdown position when input position changes or dropdown opens
  useEffect(() => {
    const updatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        // Note: Using position: fixed, so we use viewport-relative coordinates
        // getBoundingClientRect() already returns viewport-relative values,
        // so we don't add window.scrollY/scrollX
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
    };

    // Update position immediately when dropdown opens or devices change
    if (isOpen) {
    updatePosition();
    }

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, devices]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search items when input changes (but skip immediately after a selection)
  useEffect(() => {
    const searchItems = async () => {
      const lastSubmittedValue = getLastSubmittedValue();
      
      // Skip one cycle if just selected from dropdown
      if (suppressNextSearchRef.current) {
        suppressNextSearchRef.current = false;
        return;
      }
      
      // For devices: normalize input (strip non-alphanumeric characters and trim)
      // For products: just trim
      const normalizedValue = searchType === 'devices' 
        ? (value ? value.replace(/[^A-Za-z0-9]/g, '').trim() : '')
        : (value ? value.trim() : '');
      
      // Check minimum length of 2 characters
      if (!normalizedValue || normalizedValue.length < 2) {
        setDevices([]);
        setIsOpen(false);
        setSelectedIndex(-1);
        return;
      }

      try {
        setLoading(true);
        setError('');
        
        // Determine API endpoint and parameters based on searchType
        let apiEndpoint, params, responseKey;
        
        if (searchType === 'products') {
          apiEndpoint = '/api/products/search';
          params = { name: value, limit: 50 };
          responseKey = 'products';
        } else {
          // Default to 'devices'
          apiEndpoint = '/api/devices/search';
          params = { serial: value, limit: 50 };
          responseKey = 'devices';

          // Plain search: return every status, so out-of-circulation devices are
          // still findable. Repair flows only need trashed ones, to mark them as
          // non-repairable.
          if (includeAllStatuses) {
            params.include_all_statuses = true;
          } else if (disableNonRepairable) {
            params.include_wasted = true;
          }
          
          // Add product filter only for device search
        if (productId && productId !== null && productId !== undefined && productId !== 0) {
          params.product_id = productId;
        }
        }
        
        const response = await axios.get(apiEndpoint, { params });
        
        const foundItems = response.data[responseKey] || [];
        setDevices(foundItems);
        const currentLastSubmitted = getLastSubmittedValue();
        // Only open dropdown if value is different from last submitted value
        // This prevents dropdown from reopening after Enter is pressed
        if (value !== currentLastSubmitted) {
          setIsOpen(true); // Keep dropdown open to show results or "no results" message
        }
        setSelectedIndex(-1); // Don't pre-select any item - let user choose with arrow keys or mouse
      } catch (err) {
        console.error(`Error searching ${searchType}:`, err);
        setError(`Failed to search ${searchType}`);
        setDevices([]);
        setIsOpen(false);
        setSelectedIndex(-1);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchItems, 300); // Debounce search
    return () => clearTimeout(timeoutId);
  }, [value, productId, searchType, includeAllStatuses, disableNonRepairable]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    // Reset lastSubmittedValue when user starts typing again so dropdown can open
    setLastSubmittedValue(null);
    onChange(newValue);
  };

  const isItemDisabled = (item) => disableNonRepairable && NON_REPAIRABLE_DEVICE_STATUSES.includes(item?.status);

  const handleDeviceSelect = (item) => {
    // Retired/lost/blocked/trashed devices must not enter the repair procedure
    if (isItemDisabled(item)) return;

    // Prevent the next effect from re-querying with partial typed text
    suppressNextSearchRef.current = true;
    
    // Determine the value to set based on searchType and item structure
    let searchValue;
    if (searchType === 'products') {
      // For product search, use the product name
      searchValue = item.name;
    } else {
      // For device search, if selecting a product name (no serial_number), use product name
      // Otherwise use serial number
      searchValue = item.serial_number || item.product_name || item.name;
    }
    
    setLastSubmittedValue(searchValue);
    onChange(searchValue);
    setIsOpen(false);
    setSelectedIndex(-1);
    
    if (onDeviceSelect) {
      onDeviceSelect(item);
    }
  };

  // Scroll the selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Handle arrow keys and Enter/Escape when dropdown is open
    if (isOpen && devices.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex((prevIndex) => {
            // Move to next item (wrap to 0 if at end)
            return prevIndex < devices.length - 1 ? prevIndex + 1 : 0;
          });
          return; // Important: return to prevent default handling
        
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex((prevIndex) => {
            // Move to previous item (wrap to last if at beginning)
            return prevIndex > 0 ? prevIndex - 1 : devices.length - 1;
          });
          return; // Important: return to prevent default handling
        
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          // Only select if an item is actually highlighted (selectedIndex >= 0)
          if (selectedIndex >= 0 && devices[selectedIndex]) {
            handleDeviceSelect(devices[selectedIndex]);
          } else {
            // If no item selected, close the dropdown and remember this value to prevent reopening
            setLastSubmittedValue(value);
            setIsOpen(false);
            setSelectedIndex(-1);
            if (onKeyPress) {
              onKeyPress(e);
            }
          }
          return;
        
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
          setSelectedIndex(-1);
          return;
        
        default:
          // For other keys, continue with normal input handling
          break;
      }
    }
    
    // Handle Enter key when dropdown is closed or empty - close dropdown and remember value to prevent reopening
    if (e.key === 'Enter') {
      setLastSubmittedValue(value);
      setIsOpen(false);
      setSelectedIndex(-1);
    }
    
    // If dropdown not open or other keys, let parent handle if provided
    if (onKeyPress) {
      onKeyPress(e);
    }
  };

  const escapeRegExp = (input) => input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm) return text;

    const escaped = escapeRegExp(searchTerm);
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => (
      index % 2 === 1 ? (
        <strong key={index} className="font-bold text-naviter-blue">{part}</strong>
      ) : (
        part
      )
    ));
  };

  return (
    <div className="relative" style={{ zIndex: 'inherit', isolation: 'auto' }}>
      <div className="relative">
        <input
          ref={setInputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={`w-full ${size === 'sm' ? 'h-7 px-2 py-1 text-xs' : 'px-3 py-2'} border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500`}
        />
        <div className={`absolute inset-y-0 right-0 flex items-center ${size === 'sm' ? 'pr-2' : 'pr-3'}`}>
          {value ? (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
                inputRef.current?.focus();
                if (onClear) onClear();
              }}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              title="Clear"
            >
              <X className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
            </button>
          ) : (
            <div className="pointer-events-none">
              {loading ? (
                <div className={`animate-spin rounded-full ${size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} border-b-2 border-naviter-blue`}></div>
              ) : (
                <Search className={size === 'sm' ? 'h-3.5 w-3.5 text-gray-400' : 'h-4 w-4 text-gray-400'} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dropdown - using fixed position to escape stacking context */}
      {isOpen && devices.length > 0 && dropdownPosition.width > 0 && (
        <div
          ref={dropdownRef}
          className="device-autocomplete-dropdown border border-gray-300 rounded-md shadow-2xl max-h-60 overflow-auto"
          style={{ 
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 999999,
            backgroundColor: '#ffffff',
            background: '#ffffff',
            opacity: 1
          }}
        >
          {devices.map((item, index) => {
            const isDisabled = isItemDisabled(item);
            // Out-of-circulation devices read red whichever status they carry;
            // the badge alongside says which one it is.
            const showStatusBadge = !isDisabled && includeAllStatuses
              && INACTIVE_DEVICE_STATUSES.includes(item.status);
            const isFlagged = isDisabled || showStatusBadge;
            const isSelected = index === selectedIndex && !isDisabled;

            return (
            <button
                key={item.id}
                ref={(el) => (itemRefs.current[index] = el)}
              type="button"
                onClick={() => handleDeviceSelect(item)}
                onMouseEnter={() => setSelectedIndex(index)}
                data-selected={isSelected}
              className={`w-full ${size === 'sm' ? 'px-2 py-1.5' : 'px-4 py-3'} text-left border-b border-gray-100 last:border-b-0 ${isDisabled ? 'bg-red-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                    {searchType === 'products' ? (
                      <>
                        <div className={`${size === 'sm' ? 'text-xs' : 'text-sm'} font-medium text-gray-900`}>
                          {highlightMatch(item.name, value)}
                        </div>
                        {item.attribute_count !== undefined && (
                          <div className="text-xs text-gray-500">
                            {item.attribute_count} attribute{item.attribute_count !== 1 ? 's' : ''} • {item.module_count || 0} module{item.module_count !== 1 ? 's' : ''}
                          </div>
                        )}
                      </>
                    ) : item.serial_number ? (
                      <>
                  <div className={`${size === 'sm' ? 'text-xs' : 'text-sm'} font-medium ${isFlagged ? 'text-red-600' : 'text-gray-900'}`}>
                          {highlightMatch(item.serial_number, value)} : {item.product_name || 'Unknown Product'}
                          {item.igc_serial && (
                            <span className={`font-normal ${isFlagged ? '' : 'text-gray-500'}`}> · {highlightMatch(item.igc_serial, value)}</span>
                          )}
                          {showStatusBadge && (
                            <StatusBadge status={item.status} variant="device" className="ml-2 align-middle" />
                          )}
                  </div>
                        {isDisabled ? (
                          <div className="text-xs text-red-600">
                            {formatDeviceStatus(item.status)} – cannot be sent to repair
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <div className={`${size === 'sm' ? 'text-xs' : 'text-sm'} font-medium text-gray-900`}>
                        {highlightMatch(item.product_name || item.name, value)}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <div className="ml-2 flex-shrink-0">
                      <Check className={size === 'sm' ? 'h-3.5 w-3.5 text-naviter-blue' : 'h-5 w-5 text-naviter-blue'} />
                </div>
                  )}
              </div>
            </button>
            );
          })}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mt-1 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* No results message - using fixed position to escape stacking context */}
      {(() => {
        // Normalize to check if we have valid search input
        const normalizedValue = searchType === 'devices'
          ? (value ? value.replace(/[^A-Za-z0-9]/g, '').trim() : '')
          : (value ? value.trim() : '');
        const shouldShowNoResults = isOpen && normalizedValue.length >= 2 && !loading && devices.length === 0 && !error && dropdownPosition.width > 0;
        
        const itemType = searchType === 'products' ? 'products' : 'devices';
        
        return shouldShowNoResults && (
        <div className="device-autocomplete-dropdown border border-gray-300 rounded-md shadow-2xl p-3"
          style={{ 
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 999999,
            backgroundColor: '#ffffff',
            opacity: 1
          }}
        >
          <div className="text-sm text-gray-500 text-center">
              No {itemType} found matching "{value}"
            </div>
          </div>
        );
      })()}
    </div>
  );
});

DeviceAutocomplete.displayName = 'DeviceAutocomplete';

export default DeviceAutocomplete;
